import React from "react";
import { Bell, Utensils } from "lucide-react";

/* SHIMMER */

const TaskCardShimmer = () => (
  <div className="bg-white rounded-lg shadow border-l-4 p-3 border-gray-300 animate-pulse">
    <div className="flex justify-between items-center mb-2">
      <div className="h-4 bg-gray-200 rounded w-2/3" />
      <div className="h-6 w-20 bg-gray-200 rounded" />
    </div>

    <hr className="my-2 border-gray-200" />

    <div className="space-y-2 bg-gray-100 p-3 rounded text-xs">
      <div className="h-3 bg-gray-200 rounded w-5/6" />
      <div className="h-3 bg-gray-200 rounded w-4/6" />
      <div className="h-3 bg-gray-200 rounded w-3/6" />
    </div>

    <div className="mt-2 h-4 w-16 bg-gray-200 rounded" />
  </div>
);

/* TASK FILTERS */

export function TaskFilters({
  taskSearchQuery,
  setTaskSearchQuery,
  shops,
  loading,
  selectedShop1,
  setSelectedShop1,
  tempFromDate,
  tempToDate,
  setTempFromDate,
  setTempToDate,
  onApply,
  selectedStatus,
  setSelectedStatus,
}) {
  return (
    <div className="flex items-center gap-3 py-2 w-full overflow-x-auto">
      <input
        type="text"
        placeholder="Search..."
        value={taskSearchQuery}
        onChange={(e) => setTaskSearchQuery(e.target.value)}
        className="w-[350px] rounded py-2 text-base border px-3 bg-white focus:outline-none focus:ring-1 focus:ring-orange-500"
      />

      <div className="relative w-[290px] min-w-[200px]">
        <select
          value={selectedShop1}
          onChange={(e) => setSelectedShop1(e.target.value)}
          disabled={loading}
          className={`w-full px-3 py-2 border rounded text-base text-gray-500 pr-8 appearance-none focus:outline-none focus:ring-1 focus:ring-orange-500 ${
            loading ? "bg-gray-100 cursor-not-allowed" : "bg-white"
          }`}
        >
          <option value="">
            {loading ? "Loading shops..." : "All Restaurants"}
          </option>

          {Array.isArray(shops) &&
            shops.map((shop, index) => (
              <option
                key={shop.id || shop.shop_id || shop.shop_code || index}
                value={shop.code || shop.shop_code}
              >
                {shop.shop_name || shop.name}
              </option>
            ))}
        </select>
      </div>

      <input
        type="date"
        value={tempFromDate}
        onChange={(e) => setTempFromDate(e.target.value)}
        className="w-[180px] bg-white border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
      />

      <input
        type="date"
        value={tempToDate}
        onChange={(e) => setTempToDate(e.target.value)}
        className="w-[180px] bg-white border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
      />

      <button
        type="button"
        onClick={onApply}
        className="h-11 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded shadow w-[120px]"
      >
        Apply
      </button>

      <select
        value={selectedStatus}
        onChange={(e) => setSelectedStatus(e.target.value)}
        className="w-[175px] border px-2 py-3 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-orange-500"
      >
        <option value="All Status">All Status</option>
        <option value="Unassigned">Unassigned</option>
        <option value="Assigned">Assigned</option>
        <option value="Acknowledged">Acknowledged</option>
        <option value="Arrived">Arrived</option>
        <option value="Food is Ready">Food is Ready</option>
        <option value="Picked up">Picked up</option>
        <option value="Completed">Completed</option>
        <option value="Cancelled">Cancelled</option>
      </select>
    </div>
  );
}

/* TASK TABS */

const taskTabs = [
  "all",
  "unassigned",
  "assigned",
  "acknowledged",
  "arrived",
  "food is ready",
  "picked up",
  "completed",
  "cancelled",
];

export function TaskTabs({
  selectedTaskTab,
  setSelectedTaskTab,
  selectedStatus,
  setSelectedStatus,
  getTaskTabCount,
  dashboardTabsLoading = false,
}) {
  return (
    <div className="flex gap-3 py-2 items-center border bg-white shadow-sm rounded-md overflow-x-auto px-2">
      {taskTabs.map((tab) => (
        <div
          key={tab}
          onClick={() => {
            setSelectedTaskTab(tab);
          }}
          className={`cursor-pointer px-3 py-2 flex items-center gap-2 text-sm font-medium rounded-md whitespace-nowrap transition-all ${
            selectedTaskTab === tab
              ? "bg-orange-500 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          <span>
            {tab === "all"
              ? "All Orders"
              : tab === "completed"
                ? "Completed"
                : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </span>

          <span
            className={`px-3 py-1 text-xs font-bold rounded-md ${
              selectedTaskTab === tab
                ? "bg-orange-400 text-white"
                : "bg-gray-300 text-gray-800"
            }`}
          >
            {dashboardTabsLoading ? "..." : getTaskTabCount(tab)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* TASK GRID */

export function TaskGrid({
  isLoading,
  isStatusChanging,
  isTabSwitching = false,
  tasks,
  onAssignClick,
  onDetailsClick,
  SyncWithAnlook,
  getShopName,
  onInstructionClick = () => {},
  instructionTaskIds = new Set(),
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}) {
  const handleScroll = (e) => {
    const el = e.currentTarget;

    const nearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 120;

    if (nearBottom && hasMore && !isLoadingMore && !isLoading) {
      onLoadMore?.();
    }
  };

  if ((isLoading || isStatusChanging || isTabSwitching) && !isLoadingMore) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-5 mt-4">
        {Array.from({ length: 6 }).map((_, index) => (
          <TaskCardShimmer key={index} />
        ))}
      </div>
    );
  }

  if (!tasks || tasks.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <p>No tasks found for the selected filters</p>
      </div>
    );
  }

  const getCardStyling = (status) => {
    switch (status?.toLowerCase()) {
      case "unassigned":
        return { borderColor: "#FF8C00", statusBg: "bg-orange-500" };
      case "assigned":
        return { borderColor: "#3B82F6", statusBg: "bg-blue-500" };
      case "started":
        return { borderColor: "#eab308", statusBg: "bg-yellow-500" };
      case "inprogress":
        return { borderColor: "#6366F1", statusBg: "bg-indigo-500" };
      case "food is ready":
        return { borderColor: "#A855F7", statusBg: "bg-purple-500" };
      case "picked up":
        return { borderColor: "#0ea5e9", statusBg: "bg-sky-500" };
      case "completed":
      case "delivered":
      case "successful":
      case "arrived":
        return { borderColor: "#10B981", statusBg: "bg-green-500" };
      case "cancelled":
      case "failed":
      case "declined":
        return { borderColor: "#EF4444", statusBg: "bg-red-500" };
      default:
        return { borderColor: "#6B7280", statusBg: "bg-gray-500" };
    }
  };

  return (
    <div
      onScroll={handleScroll}
      className="overflow-y-auto mt-4 p-0"
      style={{ maxHeight: "calc(100vh - 280px)" }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-5">
        {tasks.map((task) => {
          const { borderColor, statusBg } = getCardStyling(task?.status);

const criticalValue = Number(task?.critical || 0);
  const isCritical = criticalValue >= 2;

          return (
            <div
      key={task?.pk_task_id || task?.id}
      className={`relative rounded-lg shadow-md border-l-4 px-5 py-3 min-h-[160px] transition-colors ${
        isCritical
          ? "bg-red-50 border border-red-500"
          : "bg-white"
      }`}
      style={{
        borderLeftColor: isCritical ? "#DC2626" : borderColor,
      }}
    >
              {/* HEADER */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <span className="text-sm font-semibold block truncate">
                    Order: {task?.order_id || "N/A"}
                  </span>

                  <span className="text-xs text-gray-600 block truncate">
                    {task?.pk_task_id} / {task?.merchant_order_id}
                  </span>
                </div>


  {isCritical && (
    <span className="mr-2 rounded-full bg-red-600 px-2 py-1 text-[10px] font-semibold text-white">
      Critical {criticalValue}
    </span>
  )}


                {(instructionTaskIds?.has(String(task.pk_task_id)) ||
                  instructionTaskIds?.has(task.pk_task_id)) && (
                  <button
                    type="button"
                    onClick={() => onInstructionClick(task.pk_task_id)}
                    className="text-orange-600 text-sm mx-4"
                  >
                    <Bell size={18} />
                  </button>
                )}

                {task?.status?.toLowerCase() === "unassigned" ? (
                  <button
                    type="button"
                    className="text-xs bg-orange-500 text-white px-3 py-1 rounded min-w-[70px] text-center"
                    onClick={() => onAssignClick(task)}
                  >
                    Assign
                  </button>
                ) : (
                  <>
                    {task.merchant_order_status === "food is ready" &&
                      task.status === "Acknowledged" && (
                        <span className="inline-flex items-center px-2 py-1 border-blue-400 text-blue-400">
                          <Utensils size={18} />
                        </span>
                      )}

                    <span
                      className={`px-3 py-1 rounded-full text-white text-xs ${statusBg}`}
                    >
                      {(task?.status || "Unknown").charAt(0).toUpperCase() +
                        (task?.status || "unknown").slice(1)}
                    </span>
                  </>
                )}
              </div>

              {/* BODY */}
              <div className="bg-gray-100 rounded-md px-3 py-2 space-y-1 text-xs min-h-[80px]">
                <div className="line-clamp-2">
                  <strong>Pickup:</strong> {task?.pickup_address || "N/A"}
                </div>

                <div className="line-clamp-2">
                  <strong>Drop:</strong> {task?.delivery_address || "N/A"}
                </div>

                {task?.driver_name && (
                  <div>
                    <strong>Driver:</strong> {task.driver_name} /{" "}
                    {task?.driver_mobile}
                  </div>
                )}

                {getShopName(task?.shopcode || task?.shop_code) && (
                  <div>
                    <strong>Restaurant:</strong>{" "}
                    {getShopName(task?.shopcode || task?.shop_code)}
                  </div>
                )}
              </div>

              {/* FOOTER BUTTONS */}
              <div className="flex justify-between items-center mt-3">
                <button
                  type="button"
                  onClick={() => SyncWithAnlook(task?.pk_task_id)}
                  className="text-orange-500 text-sm"
                >
                  ⟳
                </button>

                <button
                  type="button"
                  className="text-sm text-blue-600 underline"
                  onClick={() => onDetailsClick(task)}
                >
                  Details
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {isLoadingMore && (
        <div className="text-center text-sm text-gray-500 py-4">
          Loading more tasks...
        </div>
      )}

      {!hasMore && tasks.length > 0 && (
        <div className="text-center text-xs text-gray-400 py-4">
          No more tasks
        </div>
      )}
    </div>
  );
}